import React, { useMemo, useState } from 'react';
import loadable from '@loadable/component';

import { FormattedMessage } from '../../util/reactIntl';
import { H4, IconArrowHead, Modal } from '../../components';
import ListingImageGallery from './ListingImageGallery/ListingImageGallery';

import css from './ListingPage.module.css';
import galleryCss from './ListingImageGallery/ListingImageGallery.module.css';

const PdfViewer = loadable(() => import('../../components/PdfViewer/PdfViewer'), {
  ssr: false,
});

const SectionGallery = props => {
  const {
    listing,
    variantPrefix,
    showCertificatesOnly = false,
    authorCertificates = [],
    onManageDisableScrolling,
  } = props;
  const manageDisableScrolling = onManageDisableScrolling || (() => null);
  const images = listing?.images || [];
  const imageVariants = ['scaled-small', 'scaled-medium', 'scaled-large', 'scaled-xlarge'];
  const thumbnailVariants = [variantPrefix, `${variantPrefix}-2x`, `${variantPrefix}-4x`];
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeCertificateIndex, setActiveCertificateIndex] = useState(null);

  const modalCertificates = useMemo(() => {
    return authorCertificates.filter(cert => {
      const url = cert?.url || '';
      const urlLower = url.toLowerCase();
      const isPdf =
        cert?.resourceType === 'raw' || cert?.format === 'pdf' || urlLower.endsWith('.pdf');
      const isImage =
        !isPdf &&
        (cert?.resourceType === 'image' ||
          ['jpg', 'jpeg', 'png', 'webp'].includes(cert?.format));
      return !!(isImage || isPdf);
    });
  }, [authorCertificates]);

  const openModal = index => {
    setActiveCertificateIndex(index);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setActiveCertificateIndex(null);
  };

  const activeModalCertificate =
    activeCertificateIndex != null ? modalCertificates[activeCertificateIndex] : null;
  const hasModalNavigation = modalCertificates.length > 1;


  return showCertificatesOnly ? (
    <section className={css.sectionCertificates}>
      <H4 as="h2" className={css.sectionHeading}>
        <FormattedMessage id="ListingPage.certificatesHeading" />
      </H4>
      <div className={css.certificatesGrid}>
        {authorCertificates.map((cert, index) => {
          const url = cert.url;
          const urlLower = url.toLowerCase();
          const isPdf =
            cert.resourceType === 'raw' || cert.format === 'pdf' || urlLower.endsWith('.pdf');
          const isImage =
            !isPdf &&
            (cert.resourceType === 'image' ||
              ['jpg', 'jpeg', 'png', 'webp'].includes(cert.format));
          const label = cert.originalFilename || cert.publicId || `certificate-${index + 1}`;

          return (
            <div key={`${label}-${index}`} className={css.certificateCard}>
              {isImage ? (
                <button
                  type="button"
                  className={css.certificateImageButton}
                  onClick={() => openModal(modalCertificates.indexOf(cert))}
                >
                  <img className={css.certificateImage} src={url} alt={label} />
                </button>
              ) : (
                <div className={css.certificateFile}>
                  <embed className={css.certificatePdfEmbed} src={url} type="application/pdf" />
                  <div className={css.certificateFileMeta}>
                    <span className={css.certificateFileBadge}>PDF</span>
                    <span className={css.certificateFileName}>{label}</span>
                  </div>
                </div>
              )}
              <div className={css.certificateLinks}>
                {/* <a
                  className={css.certificateLink}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <FormattedMessage id="ListingPage.certificatesViewFile" />
                </a> */}
                {(isImage || isPdf) ? (
                  <button
                    type="button"
                    className={css.certificateLinkButton}
                    onClick={() => openModal(modalCertificates.indexOf(cert))}
                  >
                    <FormattedMessage id="ListingPage.certificateFullImage" />
                    
                  </button>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>
      {activeModalCertificate ? (
        <Modal
          id="CertificateModal"
          isOpen={isModalOpen}
          onClose={closeModal}
          onManageDisableScrolling={manageDisableScrolling}
          contentClassName={css.certificateModalContent}
        >
          <div className={css.certificateModalInner}>
            {hasModalNavigation ? (
              <button
                className={galleryCss.navLeft}
                onClick={() =>
                  setActiveCertificateIndex(
                    (activeCertificateIndex - 1 + modalCertificates.length) %
                      modalCertificates.length
                  )
                }
              >
                <div className={galleryCss.navArrowWrapper}>
                  <IconArrowHead direction="left" size="big" />
                </div>
              </button>
            ) : null}
            {(() => {
              const url = activeModalCertificate.url;
              const urlLower = url.toLowerCase();
              const isPdf =
                activeModalCertificate.resourceType === 'raw' ||
                activeModalCertificate.format === 'pdf' ||
                urlLower.endsWith('.pdf');

              return isPdf ? (
                <PdfViewer url={url} />
                // <h1>hello</h1>
              ) : (
                <img
                  className={css.certificateModalImage}
                  src={url}
                  alt={activeModalCertificate.originalFilename || 'Certificate'}
                />
              );
            })()}
            {hasModalNavigation ? (
              <button
                className={galleryCss.navRight}
                onClick={() =>
                  setActiveCertificateIndex((activeCertificateIndex + 1) % modalCertificates.length)
                }
              >
                <div className={galleryCss.navArrowWrapper}>
                  <IconArrowHead direction="right" size="big" />
                </div>
              </button>
            ) : null}
          </div>
        </Modal>
      ) : null}
    </section>
  ) : (
    <section className={css.productGallery} data-testid="carousel">
      <ListingImageGallery
        images={images}
        imageVariants={imageVariants}
        thumbnailVariants={thumbnailVariants}
      />
    </section>
  );
};

export default SectionGallery;
